import loadable from '@loadable/component';
import { useState } from 'react';

import { Box, Grid, Heading, Text, useTheme } from '@simplywallst/ui-core';

import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';

import { pluralise } from '@/utilities/pluralise';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { RisksAndRewards } from './RisksAndRewards';
import { Snowflake } from './Snowflake';

const InformationModal = loadable(() => import('./InformationModal'), {
  resolveComponent: (c) => c.InformationModal,
});

interface Props {
  portfolio: Portfolio;
}

export const OverviewSnowflake = ({ portfolio }: Props) => {
  const theme = useTheme();

  const { holdings } = useHoldingsLimits({
    portfolioHoldings: portfolio.holdings,
  });

  const [showInformationModal, setShowInformationModal] = useState(false);

  const holdingOrHoldings = pluralise(holdings.length, 'holding', 'holdings');

  const { risksCount, rewardsCount } = portfolio.analysis;

  const hasRisks = risksCount !== null;
  const hasRewards = rewardsCount !== null;

  return (
    <Grid gap="16px">
      <Grid
        templateColumns="auto min-content"
        templateRows="24px"
        alignItems="center"
      >
        <Heading as="h2" typography="smallStrong" color="white">
          Portfolio Snowflake
        </Heading>
        <button onClick={() => setShowInformationModal(true)}>
          <InformationSolid fill={theme.color.whiteRGBA(0.5)} />
        </button>
        {showInformationModal && (
          <InformationModal onClose={() => setShowInformationModal(false)} />
        )}
      </Grid>

      <Grid placeContent="center">
        <Box width={220} data-testid="snowflake-overview">
          <Snowflake portfolio={portfolio} />
        </Box>
      </Grid>
      {portfolio.isAnalysisRestricted
        ? null
        : portfolio.snowflake.description && (
            <Text typography="xSmall">{portfolio.snowflake.description}</Text>
          )}

      <Grid
        templateColumns="max-content auto"
        justifyContent="start"
        alignItems="center"
        gap="16px"
      >
        <Text typography="xSmall" color="whiteRGBA" opacity={0.7}>
          {portfolio.isAnalysisRestricted
            ? '0 holdings'
            : `${holdings.length} ${holdingOrHoldings}`}
        </Text>
        {(hasRisks || hasRewards) && <RisksAndRewards portfolio={portfolio} />}
      </Grid>
    </Grid>
  );
};
